import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import FloatingBackground from "./FloatingBackground";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.0, delay: 0.15 + i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const MaskedLine = ({ children, i = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      variants={line}
      custom={i}
      className={`block will-change-transform ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGhost = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -140]);
  const yTonek = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -80]);
  const rGhost = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 8]);
  const yWord = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -80]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] pt-28 md:pt-32 pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      <FloatingBackground />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex items-center justify-between text-xs md:text-sm text-[#0b2a55]/70 font-semibold tracking-[0.24em] uppercase"
        >
          <span data-testid="hero-meta-left" className="chip">
            <span className="dot" /> Rozdział 001 — Zima 2025
          </span>
          <span data-testid="hero-meta-right" className="hidden md:inline">
            Bajkowa marka · Made with wonder in PL
          </span>
        </motion.div>

        <motion.h1
          initial="hidden"
          animate="show"
          className="font-display font-semibold text-[#051937] mt-10 md:mt-14 text-[3.2rem] sm:text-[4.8rem] md:text-[6.6rem] lg:text-[8.4rem] leading-[0.9] tracking-[-0.03em]"
          data-testid="hero-title"
        >
          <MaskedLine i={0}>Bajkowy świat</MaskedLine>
          <MaskedLine i={1}>
            <span className="inline-flex items-baseline gap-4 md:gap-6">
              <motion.span
                style={{ y: yWord }}
                className="font-script text-[#60A5FA] text-[3rem] sm:text-[4.2rem] md:text-[5.6rem] lg:text-[7.2rem] leading-none translate-y-2 md:translate-y-4"
              >
                Tonka
              </motion.span>
              <span>i</span>
              <motion.span
                style={{ y: yWord }}
                className="font-script text-[#60A5FA] text-[3rem] sm:text-[4.2rem] md:text-[5.6rem] lg:text-[7.2rem] leading-none translate-y-2 md:translate-y-4"
              >
                Duszka
              </motion.span>
            </span>
          </MaskedLine>
          <MaskedLine i={2}>
            <span className="inline-flex items-center gap-4">
              zaczyna się
              <span className="hidden md:inline-block w-32 h-[2px] bg-[#051937]/40 align-middle" />
            </span>
          </MaskedLine>
          <MaskedLine i={3}>
            <em className="not-italic text-[#60A5FA]"> tu.</em>
          </MaskedLine>
        </motion.h1>

        <div className="mt-14 md:mt-20 grid grid-cols-12 gap-6 md:gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.9 }}
            className="col-span-12 md:col-span-5 md:col-start-1"
          >
            <p className="text-lg md:text-xl leading-relaxed text-[#0b2a55]/85 max-w-md">
              Tonek to ciekawski chłopiec. Duszek to jego najlepszy przyjaciel — mały,
              biały, potrafi latać i znikać, kiedy tylko chce.
              Razem odkrywają, że najpiękniejsze historie zaczynają się od zwykłego{" "}
              <span className="font-script text-2xl text-[#60A5FA]">„a co jeśli…”</span>
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <motion.a
                href="#bajki"
                data-testid="hero-cta-primary"
                whileHover={{ scale: 1.05, rotate: [-2, 2, -2, 0] }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.35 }}
                className="inline-flex items-center gap-2 rounded-full bg-[#051937] hover:bg-[#0b2a55] text-white px-7 py-4 font-semibold shadow-[0_20px_50px_-20px_rgba(11,42,85,0.6)]"
              >
                <Sparkles size={18} /> Czytaj pierwszą bajkę
              </motion.a>
              <motion.a
                href="#bohaterowie"
                data-testid="hero-cta-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur-md border border-[#BAE6FD] text-[#051937] px-6 py-4 font-semibold"
              >
                Poznaj bohaterów <ArrowDown size={16} />
              </motion.a>
            </div>
          </motion.div>

          {/* Duo showcase */}
          <div className="col-span-12 md:col-span-6 md:col-start-7 relative min-h-[380px] md:min-h-[460px]">
            <motion.div
              style={{ y: yTonek }}
              className="absolute left-0 md:left-2 top-0 md:top-6 w-[52%] max-w-[240px]"
              data-testid="hero-tonek"
            >
              <motion.img
                src="/assets/portrait-tonek.png"
                alt="Tonek"
                animate={reduced ? undefined : { y: [-8, 8, -8], rotate: [-2, 2, -2] }}
                transition={
                  reduced
                    ? undefined
                    : { duration: 5, repeat: Infinity, ease: "easeInOut" }
                }
                className="w-full h-auto drop-shadow-[0_30px_40px_rgba(11,42,85,0.25)]"
              />
              <motion.span
                animate={reduced ? undefined : { rotate: [-6, 6, -6] }}
                transition={reduced ? undefined : { duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -left-2 top-4 chip !bg-[#FDBA74] !border-[#FDBA74]/60 text-[#3B1B00]"
              >
                cześć, jestem Tonek
              </motion.span>
            </motion.div>

            <motion.div
              style={{ y: yGhost, rotate: rGhost }}
              className="absolute right-0 top-14 md:top-20 w-[52%] max-w-[240px]"
              data-testid="hero-duszek"
            >
              <motion.img
                src="/assets/portrait-duszek.png"
                alt="Duszek"
                animate={reduced ? undefined : { y: [-14, 14, -14] }}
                transition={
                  reduced
                    ? undefined
                    : { duration: 4, repeat: Infinity, ease: "easeInOut" }
                }
                className="w-full h-auto drop-shadow-[0_30px_40px_rgba(96,165,250,0.35)]"
              />
              <motion.span
                animate={reduced ? undefined : { rotate: [3, -3, 3] }}
                transition={reduced ? undefined : { duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -right-1 bottom-8 chip !bg-[#FDE68A] !border-[#FDE68A]/60 text-[#051937]"
              >
                ✨ jestem Duszek
              </motion.span>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="mt-16 flex items-center justify-between text-xs md:text-sm text-[#0b2a55]/70 font-semibold tracking-[0.2em] uppercase"
        >
          <span>Przewiń</span>
          <div className="hidden md:flex items-center gap-3">
            <span className="dot bg-[#84C5F4]" />
            <span>Bohaterowie</span>
            <span className="dot bg-[#BAE6FD]" />
            <span>Bajki</span>
            <span className="dot bg-[#FDE68A]" />
            <span>Manifest</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
